import React, { useState } from "react";
import { makeStyles } from "tss-react/mui";
import { Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ListItemText from "@mui/material/ListItemText";
import {
  Box,
  Chip,
  IconButton,
  Link,
  ListItemButton,
  Paper,
  Tooltip,
  Collapse,
} from "@mui/material";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import { NavLink, useNavigate } from "react-router-dom";
import InviteDialog from "./InviteDialog";
import HomeIcon from "@mui/icons-material/Home";
import TrialAndPlan from "./TrialAndPlan";
import WebhookIcon from "@mui/icons-material/Webhook";
//import { TrackGoogleAnalyticsEvent } from "../googleAnalytics";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import PeopleIcon from "@mui/icons-material/People";
import { useUser } from "../contexts/useUser";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useQueryClient } from "@tanstack/react-query";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import TopicIcon from "@mui/icons-material/Topic";
import ChatIcon from "@mui/icons-material/Chat";
import GravatarAvatar from "./GravatarAvatar";
import { useAuthenticator } from "@aws-amplify/ui-react";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

const useStyles = makeStyles()((theme: Theme) => ({
  list: {
    width: 250,
  },
  menuButton: {
  },

  listContainer: {
    flexGrow: 1,
    overflowY: "auto",
    height: "100%",
    paddingBottom: "120px",
  },

  fixedFooter: {
    position: "fixed",
    bottom: 0,
    left: 0,
    width: "250px", // Match the sidebar width
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    zIndex: 1,
    boxSizing: "border-box",
  },

  link: {
    color: theme.palette.text.primary,
  },

  activeLink: {
    "& .MuiListItemButton-root": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? `${theme.palette.primary.main}24`
          : `${theme.palette.primary.main}12`,
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.divider}`,
    },
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.main,
    },
  },

  compactListItem: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    "& .MuiListItemButton-root": {
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      minHeight: "36px", // Reduced from default ~48px
    },
    "& .MuiListItemIcon-root": {
      minWidth: "36px", // Reduced icon spacing
    },
  },
}));

// allows styling the menu items based on the active route
const MyNavLink = React.forwardRef<any, any>((props, ref) => (
  <NavLink
    ref={ref}
    to={props.to}
    className={({ isActive }) =>
      `${props.className} ${isActive && props.to ? props.activeClassName : ""}`
    }
  >
    {props.children}
  </NavLink>
));

const MenuPanel = () => {
  const { classes } = useStyles();
  const { user, projects, project, setUser, trialDaysRemaining, entitlements } =
    useUser();
  const navigate = useNavigate();
  const { signOut, user: contextUser } = useAuthenticator((context) => [
    context.user,
  ]);
  const { route } = useAuthenticator((context) => [context.route]);

  const queryClient = useQueryClient();

  const [inviteDialogOpen, setInviteDialogOpen] = useState<boolean>(false);
  
  // Admin menu collapse state with localStorage persistence
  const [adminMenuExpanded, setAdminMenuExpanded] = useState<boolean>(() => {
    const saved = localStorage.getItem('_llmasaservice_admin_menu_expanded');
    return saved ? JSON.parse(saved) : false;
  });

  const toggleAdminMenu = () => {
    const newState = !adminMenuExpanded;
    setAdminMenuExpanded(newState);
    localStorage.setItem('_llmasaservice_admin_menu_expanded', JSON.stringify(newState));
  };

  const handleSignOutClick = async () => {
    signOut();
    navigate("/login");
  };

  const THEME_KEY = "_llmasaservice_theme";
  const getTheme = () => {
    const storedTheme = localStorage.getItem(THEME_KEY);

    if (!storedTheme) {
      localStorage.setItem(THEME_KEY, "dark");
      return "dark";
    }

    return storedTheme;
  };

  const toggleTheme = () => {
    const ls = getTheme();
    localStorage.setItem(THEME_KEY, ls === "light" ? "dark" : "light");
    window.location.reload();
  };

  return (
    <Paper sx={{ height: "100vh", position: "relative", overflow: "hidden" }}>
      <Box
        sx={{
          height: "50px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {" "}
        {route === "authenticated" ? (
          <GravatarAvatar
            email={contextUser.attributes?.email || ""}
            size={40}
            onLogout={handleSignOutClick}
          />
        ) : null}
        <Select
          fullWidth
          value={user?.id || ""}
          sx={{
            border: "none",
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          }}
        >
          {projects?.map((project) => (
            <MenuItem
              key={project.id}
              value={project.id}
              onClick={() => {
                try {
                  localStorage.setItem("selectedProjectId", project?.id ?? "");
                  setUser(project);
                  queryClient.invalidateQueries();
                  console.log("Switched to project:", project.project?.name);
                  navigate("/");
                } catch (error) {
                  console.error("Error switching project:", error);
                }
              }}
            >
              {project.project?.name}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <List
        dense
        className={classes.listContainer}
        sx={{ 
          width: "100%", 
          bgcolor: "background.paper", 
          overflow: "auto",
          "& .MuiListItem-root": {
            paddingTop: 0.5,
            paddingBottom: 0.5,
          },
          "& .MuiListItemButton-root": {
            paddingTop: 0.5,
            paddingBottom: 0.5,
            minHeight: "36px",
          },
          "& .MuiListItemIcon-root": {
            minWidth: "36px",
          },
        }}
      >
        <ListItem>
          <TrialAndPlan />
        </ListItem>
        
        {/* Main menu items */}
        <ListItem
          key="home"
          component={MyNavLink}
          to="/"
          className={`${classes.link} ${classes.compactListItem}`}
          activeClassName={classes.activeLink}
        >
          <ListItemButton>
            <ListItemIcon>
              <HomeIcon />
            </ListItemIcon>
            <ListItemText primary="Home" />
          </ListItemButton>
        </ListItem>
        
        <ListItem
          key="customers"
          component={MyNavLink}
          to="/customers"
          className={`${classes.link} ${classes.compactListItem}`}
          activeClassName={classes.activeLink}
        >
          <ListItemButton>
            <ListItemIcon>
              <PeopleIcon />
            </ListItemIcon>
            <ListItemText primary="Customers" />
          </ListItemButton>
        </ListItem>
        
        <ListItem
          key="conversations"
          component={MyNavLink}
          to="/conversations"
          className={`${classes.link} ${classes.compactListItem}`}
          activeClassName={classes.activeLink}
        >
          <ListItemButton>
            <ListItemIcon>
              <ChatIcon />
            </ListItemIcon>
            <ListItemText primary="Conversations" />
          </ListItemButton>
        </ListItem>
        
        <ListItem
          key="agents"
          component={MyNavLink}
          to="/agents"
          className={`${classes.link} ${classes.compactListItem}`}
          activeClassName={classes.activeLink}
        >
          <ListItemButton>
            <ListItemIcon>
              <AutoAwesomeIcon />
            </ListItemIcon>
            <ListItemText primary="Agents" />
          </ListItemButton>
        </ListItem>

        <ListItem
          key="enterprise"
          component={MyNavLink}
          to={
            entitlements?.includes("enterprise-chat")
              ? "/enterpriseconfig"
              : "/enterpriseconfig"
          }
          className={`${classes.link} ${classes.compactListItem}`}
          activeClassName={classes.activeLink}
        >
          <ListItemButton disabled={!entitlements?.includes("enterprise-chat")}>
            <ListItemIcon>
              <AccountBalanceIcon />
            </ListItemIcon>
            <ListItemText primary="Enterprise LLM" />
            <Chip
              size="small"
              label="ent"
              title="Enterprise level feature"
              color={
                entitlements?.includes("enterprise-chat")
                  ? "default"
                  : "warning"
              }
            />
          </ListItemButton>
        </ListItem>

        {/* Admin menu header */}
        <ListItem key="admin-header" className={classes.compactListItem}>
          <ListItemButton onClick={toggleAdminMenu}>
            <ListItemIcon>
              <AdminPanelSettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Admin" />
            {adminMenuExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItemButton>
        </ListItem>

        {/* Collapsible Admin submenu */}
        <Collapse in={adminMenuExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ 
            '& .MuiListItem-root': { 
              py: 0.25,
              "& .MuiListItemButton-root": {
                paddingTop: 0.25,
                paddingBottom: 0.25,
                minHeight: "32px", // Even more compact for submenu
              },
            } 
          }}>
            <ListItem
              key="services"
              component={MyNavLink}
              to="/services"
              className={classes.link}
              activeClassName={classes.activeLink}
              sx={{ pl: 4 }}
            >              <ListItemButton>
                <ListItemIcon>
                  <MiscellaneousServicesIcon />
                </ListItemIcon>
                <ListItemText primary="Models" />
              </ListItemButton>
            </ListItem>
            
            <ListItem
              key="route"
              component={MyNavLink}
              to="/route"
              className={classes.link}
              activeClassName={classes.activeLink}
              sx={{ pl: 4 }}
            >
              <ListItemButton>
                <ListItemIcon>
                  <AltRouteIcon />
                </ListItemIcon>
                <ListItemText primary="Route" />
              </ListItemButton>
            </ListItem>
            
            <ListItem
              key="triggers"
              component={MyNavLink}
              to="/triggers"
              className={classes.link}
              activeClassName={classes.activeLink}
              sx={{ pl: 4 }}
            >
              <ListItemButton>
                <ListItemIcon>
                  <FlashOnIcon />
                </ListItemIcon>
                <ListItemText primary="Triggers" />
              </ListItemButton>
            </ListItem>
            
            <ListItem
              key="prompts"
              component={MyNavLink}
              to="/prompts"
              className={classes.link}
              activeClassName={classes.activeLink}
              sx={{ pl: 4 }}
            >
              <ListItemButton>
                <ListItemIcon>
                  <LibraryBooksIcon />
                </ListItemIcon>
                <ListItemText primary="Prompt Library" />
              </ListItemButton>
            </ListItem>
            
            <ListItem
              key="usage-tiers"
              component={MyNavLink}
              to="/usage-tiers"
              className={classes.link}
              activeClassName={classes.activeLink}
              sx={{ pl: 4 }}
            >
              <ListItemButton>
                <ListItemIcon>
                  <AccountTreeIcon />
                </ListItemIcon>
                <ListItemText primary="Usage Tiers" />
              </ListItemButton>
            </ListItem>
            
            <ListItem
              key="chat"
              component={MyNavLink}
              to="/chat"
              className={classes.link}
              activeClassName={classes.activeLink}
              sx={{ pl: 4 }}
            >
              <ListItemButton>
                <ListItemIcon>
                  <ChatBubbleOutlineIcon />
                </ListItemIcon>
                <ListItemText primary="Chat Test" />
              </ListItemButton>
            </ListItem>

            <ListItem
              key="quality"
              component={MyNavLink}
              to="/quality"
              className={classes.link}
              activeClassName={classes.activeLink}
              sx={{ pl: 4 }}
            >
              <ListItemButton>
                <ListItemIcon>
                  <TroubleshootIcon />
                </ListItemIcon>
                <ListItemText primary="Review calls" />
              </ListItemButton>
            </ListItem>
            
            <ListItem
              key="logs"
              component={MyNavLink}
              to="/logging"
              className={classes.link}
              activeClassName={classes.activeLink}
              sx={{ pl: 4 }}
            >
              <ListItemButton>
                <ListItemIcon>
                  <TopicIcon />
                </ListItemIcon>
                <ListItemText primary="Call Logs" />
              </ListItemButton>
            </ListItem>
            
            <ListItem
              key="integration"
              component={MyNavLink}
              to="/integration"
              className={classes.link}
              activeClassName={classes.activeLink}
              sx={{ pl: 4 }}
            >
              <ListItemButton>
                <ListItemIcon>
                  <WebhookIcon />
                </ListItemIcon>
                <ListItemText primary="Integration" />
              </ListItemButton>
            </ListItem>
          </List>
        </Collapse>


        <InviteDialog open={inviteDialogOpen} setOpen={setInviteDialogOpen} />
        <ListItem key="invite-dlg" className={classes.compactListItem}>
          <ListItemButton
            onClick={() => {
              setInviteDialogOpen(true);
            }}
          >
            <ListItemIcon>
              <PersonAddOutlinedIcon />
            </ListItemIcon>
            <ListItemText primary="Invite colleagues" />
          </ListItemButton>
        </ListItem>
      </List>

      <Box
        className={classes.fixedFooter}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "250px", // Match Paper width
        }}
      >
        <Link
          href="https://llmasaservice.io"
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
          sx={{
            fontSize: "0.8em",
            display: "flex",
            alignItems: "center",
            color: "text.secondary", // Uses theme's text color
            flexGrow: 1,
          }}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 72 72"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ marginRight: "5px" }}
          >
            <ellipse
              cx="14.0868"
              cy="59.2146"
              rx="7.8261"
              ry="7.7854"
              fill="#2487D8"
            />
            <ellipse
              cx="24.9013"
              cy="43.0776"
              rx="6.11858"
              ry="6.08676"
              fill="#2487D8"
            />
            <ellipse
              cx="45.391"
              cy="43.0776"
              rx="6.11858"
              ry="6.08676"
              fill="#2487D8"
            />
            <ellipse
              cx="65.8813"
              cy="43.0776"
              rx="6.11858"
              ry="6.08676"
              fill="#2487D8"
            />
            <ellipse
              cx="13.9444"
              cy="30.4795"
              rx="4.83795"
              ry="4.81279"
              fill="#2487D8"
            />
            <ellipse
              cx="34.7193"
              cy="30.4795"
              rx="4.83795"
              ry="4.81279"
              fill="#2487D8"
            />
            <ellipse
              cx="55.4942"
              cy="30.4795"
              rx="4.83795"
              ry="4.81279"
              fill="#2487D8"
            />
            <ellipse
              cx="3.27273"
              cy="20.4293"
              rx="3.27273"
              ry="3.25571"
              fill="#2487D8"
            />
            <ellipse
              cx="24.9011"
              cy="20.4293"
              rx="3.27273"
              ry="3.25571"
              fill="#2487D8"
            />
            <ellipse
              cx="45.3914"
              cy="20.4293"
              rx="3.27273"
              ry="3.25571"
              fill="#2487D8"
            />
            <ellipse
              cx="12.2373"
              cy="13.4931"
              rx="1.70751"
              ry="1.69863"
              fill="#2487D8"
            />
            <ellipse
              cx="33.0122"
              cy="13.4931"
              rx="1.70751"
              ry="1.69863"
              fill="#2487D8"
            />
            <ellipse
              cx="53.5019"
              cy="13.4931"
              rx="1.70751"
              ry="1.69863"
              fill="#2487D8"
            />
            <ellipse
              cx="19.3517"
              cy="6.13242"
              rx="1.13834"
              ry="1.13242"
              fill="#2487D8"
            />
            <ellipse
              cx="40.1266"
              cy="6.13242"
              rx="1.13834"
              ry="1.13242"
              fill="#2487D8"
            />
            <ellipse
              cx="60.901"
              cy="6.13242"
              rx="1.13834"
              ry="1.13242"
              fill="#2487D8"
            />
            <ellipse
              cx="34.8617"
              cy="59.2146"
              rx="7.8261"
              ry="7.7854"
              fill="#2487D8"
            />
            <ellipse
              cx="55.6366"
              cy="59.2146"
              rx="7.8261"
              ry="7.7854"
              fill="#ED7D31"
            />
          </svg>
          <Typography variant="caption">
            llmasaservice.io by <br />
            Predictability at Scale, Inc.
          </Typography>
        </Link>
        <Tooltip
          title={
            getTheme() === "dark"
              ? "Switch to light theme"
              : "Switch to dark theme"
          }
        >
          <IconButton onClick={toggleTheme} size="small">
            {getTheme() === "dark" ? (
              <LightModeIcon fontSize="small" />
            ) : (
              <DarkModeIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
};
export default MenuPanel;
